<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-broom me-2"></i>{{ $t('page.bed_cleaning', 'Bed Cleaning Queue') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-amber" @click="autoRefresh = !autoRefresh">
            <i class="mdi" :class="autoRefresh ? 'mdi-pause' : 'mdi-play'"></i>
            {{ autoRefresh ? 'Pause' : 'Auto-refresh' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="scope='open'" :class="{ selected: scope==='open' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.pending || 0 }}</div>
          <div class="hms-stat-lbl">Pending</div>
        </div>
        <div class="hms-stat" @click="scope='open'" :class="{ selected: scope==='open' }">
          <div class="hms-stat-num hms-stat-info">{{ stats.in_progress || 0 }}</div>
          <div class="hms-stat-lbl">In Progress</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-danger">{{ stats.overdue || 0 }}</div>
          <div class="hms-stat-lbl">Overdue (SLA)</div>
        </div>
        <div class="hms-stat" @click="scope='completed'" :class="{ selected: scope==='completed' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.completed_today || 0 }}</div>
          <div class="hms-stat-lbl">Completed Today</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ Math.round(stats.avg_turnover_minutes || 0) }}m</div>
          <div class="hms-stat-lbl">Avg Turnover (7d)</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: scope==='open' }"      @click="scope='open'; loadAll()">
          <i class="mdi mdi-format-list-checks me-1"></i>Open
        </button>
        <button class="hms-tab" :class="{ active: scope==='mine' }"      @click="scope='mine'; loadAll()">
          <i class="mdi mdi-account-hard-hat me-1"></i>My Tasks
        </button>
        <button class="hms-tab" :class="{ active: scope==='completed' }" @click="scope='completed'; loadAll()">
          <i class="mdi mdi-check-circle-outline me-1"></i>Completed (24h)
        </button>
      </div>

      <div v-if="loading && !rows.length" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else-if="rows.length" class="hms-list">
        <div v-for="r in rows" :key="r.cleaning_id" class="hms-task" :class="taskClass(r)">
          <div class="hms-task-head">
            <div class="hms-task-bed">
              <i class="mdi mdi-bed"></i>
              <strong>{{ r.ward_name || 'Ward' }} · Bed {{ r.bed_number }}</strong>
              <span class="hms-muted-sm" v-if="r.bed_type">{{ r.bed_type }}</span>
            </div>
            <span class="hms-badge ms-auto" :class="statusBadge(r.status)">{{ r.status }}</span>
            <span class="hms-time-pill" :class="r.is_overdue == 1 ? 'overdue' : ''">
              <i class="mdi mdi-timer-outline"></i>
              {{ r.status === 'completed' ? formatMins(r.total_minutes) : formatMins(r.minutes_open) + ' / ' + r.sla_minutes + 'm' }}
            </span>
          </div>

          <div class="hms-task-body">
            <div class="hms-muted-sm">
              <i class="mdi mdi-clock-outline"></i> Requested {{ formatDateTime(r.requested_at) }}
              <span v-if="r.requested_by_name"> by {{ r.requested_by_name }}</span>
            </div>
            <div class="hms-muted-sm" v-if="r.assigned_to_name">
              <i class="mdi mdi-account-hard-hat"></i> Assigned to <strong>{{ r.assigned_to_name }}</strong>
              <span v-if="r.started_at"> · started {{ formatDateTime(r.started_at) }}</span>
            </div>
            <div class="hms-muted-sm" v-if="r.completed_at">
              <i class="mdi mdi-check-circle-outline text-success"></i> Completed {{ formatDateTime(r.completed_at) }}
            </div>
            <div class="hms-muted-sm" v-if="r.notes" style="white-space:pre-wrap;">
              <i class="mdi mdi-note-text-outline"></i> {{ r.notes }}
            </div>
          </div>

          <div class="hms-task-actions" v-if="r.status !== 'completed'">
            <button v-if="!r.assigned_to" class="hms-btn hms-btn-primary" @click="claim(r)" :disabled="processing">
              <i class="mdi mdi-hand-back-right me-1"></i>Claim
            </button>
            <button class="hms-btn hms-btn-blue" @click="openAssign(r)" :disabled="processing">
              <i class="mdi mdi-account-arrow-right-outline me-1"></i>{{ r.assigned_to ? 'Reassign' : 'Assign' }}
            </button>
            <button class="hms-btn hms-btn-success" @click="openComplete(r)" :disabled="processing">
              <i class="mdi mdi-check-decagram me-1"></i>Mark Clean
            </button>
          </div>
        </div>
      </div>

      <div v-else class="hms-card hms-empty">
        <i class="mdi mdi-broom"></i>
        {{ scope === 'mine' ? 'No tasks assigned to you.' : (scope === 'completed' ? 'Nothing completed in the last 24h.' : 'Cleaning queue is empty.') }}
      </div>

      <!-- ASSIGN MODAL -->
      <div class="hms-modal-overlay" v-if="assignTarget" @click.self="assignTarget=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-arrow-right-outline me-1"></i>Assign Cleaning</span>
            <button class="hms-modal-close" @click="assignTarget=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            <strong>{{ assignTarget.ward_name }} · Bed {{ assignTarget.bed_number }}</strong> — pick a housekeeper or nurse.
          </div>
          <label class="hms-label">Assignee *</label>
          <select class="hms-input mb-3" v-model="assignTo">
            <option value="">— Select —</option>
            <option v-for="s in staff" :key="s.user_id" :value="s.user_id">
              {{ s.first_name }} {{ s.last_name }} <span v-if="s.role_name">— {{ s.role_name }}</span>
            </option>
          </select>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="assignTarget=null">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="confirmAssign" :disabled="processing || !assignTo">Assign</button>
          </div>
        </div>
      </div>

      <!-- COMPLETE MODAL -->
      <div class="hms-modal-overlay" v-if="completeTarget" @click.self="completeTarget=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-check-decagram me-1"></i>Mark Bed Clean</span>
            <button class="hms-modal-close" @click="completeTarget=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3" :class="completeTarget.is_overdue ? 'hms-banner-danger' : ''">
            <i class="mdi" :class="completeTarget.is_overdue == 1 ? 'mdi-alert' : 'mdi-information-outline'"></i>
            <strong> {{ completeTarget.ward_name }} · Bed {{ completeTarget.bed_number }}</strong>
            <span v-if="completeTarget.is_overdue == 1"> — task is overdue ({{ formatMins(completeTarget.minutes_open) }} open vs {{ completeTarget.sla_minutes }}m SLA).</span>
            <span v-else> — open {{ formatMins(completeTarget.minutes_open) }}.</span>
            <div class="hms-muted-sm mt-1">After confirming, the bed becomes <strong>available</strong> for the next admission.</div>
          </div>
          <label class="hms-label">Notes (optional)</label>
          <textarea class="hms-input mb-3" rows="2" v-model="completeNotes" placeholder="e.g. Linen changed, mattress disinfected, equipment restocked..."></textarea>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="completeTarget=null">Cancel</button>
            <button class="hms-btn hms-btn-success flex-fill" @click="confirmComplete" :disabled="processing">Confirm Clean</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.text-success { color: var(--hms-success); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-info    { color: var(--hms-info); }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-danger  { color: var(--hms-danger); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-empty { text-align: center; padding: 60px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 64px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* Task cards */
.hms-list { display: flex; flex-direction: column; gap: 10px; }
.hms-task { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-left: 6px solid var(--hms-warning); border-radius: 12px; padding: 14px 16px; transition: all .15s; }
.hms-task:hover { transform: translateX(2px); }
.hms-task.st-pending     { border-left-color: var(--hms-warning); }
.hms-task.st-in_progress { border-left-color: var(--hms-info); }
.hms-task.st-overdue     { border-left-color: var(--hms-danger); background: rgba(220,53,69,.04); }
.hms-task.st-completed   { border-left-color: var(--hms-success); opacity: 0.85; }

.hms-task-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
.hms-task-bed  { display: flex; align-items: center; gap: 8px; color: var(--hms-h); font-size: 14px; }
.hms-task-bed i { color: var(--hms-primary); font-size: 20px; }
.hms-task-body { display: flex; flex-direction: column; gap: 4px; padding: 6px 0; border-top: 1px dashed var(--hms-br); }
.hms-task-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 8px; margin-top: 8px; border-top: 1px dashed var(--hms-br); }

.hms-time-pill { font-size: 11px; color: var(--hms-h); padding: 2px 10px; border: 1px solid var(--hms-br); border-radius: 50px; }
.hms-time-pill.overdue { color: var(--hms-danger); border-color: var(--hms-danger); background: rgba(220,53,69,.08); font-weight: 700; }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-success { background: var(--hms-success); color: #fff; }
.hms-btn-success:hover:not(:disabled) { filter: brightness(1.1); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner.hms-banner-danger { background: rgba(220,53,69,.08); color: var(--hms-danger); border-color: rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "BedCleaningQueue",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      autoRefresh: true, _timer: null,
      scope: 'open',
      rows: [], stats: {},
      staff: [],

      assignTarget: null, assignTo: '',
      completeTarget: null, completeNotes: '',

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
    this.loadStaff();
    this._timer = setInterval(() => { if (this.autoRefresh) this.loadAll(true); }, 30000);
  },
  beforeUnmount() {
    if (this._timer) clearInterval(this._timer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => self.toastMsg = '', 2400);
    },

    async loadAll(silent) {
      if (!silent) this.loading = true;
      try {
        var r = await executeJson({ function: "getCleaningQueue", scope: this.scope }, hms);
        if (r.data && r.data.success) {
          this.rows  = (r.data.data && r.data.data.CleaningQueue) || [];
          this.stats = (r.data.data && r.data.data.Stats)         || {};
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadStaff() {
      try {
        var r = await executeJson({ function: "getHousekeepingStaff" }, hms);
        if (r.data && r.data.success) this.staff = (r.data.data && r.data.data.Staff) || [];
      } catch(e) {}
    },

    taskClass(r) {
      if (r.is_overdue == 1) return 'st-overdue';
      return 'st-' + r.status;
    },
    statusBadge(s) {
      return ({ pending:'hms-badge-warning', in_progress:'hms-badge-info', overdue:'hms-badge-danger', completed:'hms-badge-success' })[s] || 'hms-badge-warning';
    },
    formatMins(m) { if (m == null) return '0m'; if (m < 60) return Math.round(m) + 'm'; var h = Math.floor(m / 60); return h + 'h ' + Math.round(m - h*60) + 'm'; },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },

    async claim(r) {
      this.processing = true;
      try {
        var resp = await executeJson({ function: "claimBedCleaning", cleaning_id: r.cleaning_id }, hms);
        if (resp.data && resp.data.success) { this.showToast("Task claimed"); await this.loadAll(true); }
        else this.showToast(resp.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openAssign(r) { this.assignTarget = r; this.assignTo = r.assigned_to || ''; },
    async confirmAssign() {
      if (!this.assignTo) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "assignBedCleaning", cleaning_id: this.assignTarget.cleaning_id, assignee_id: this.assignTo }, hms);
        if (r.data && r.data.success) { this.showToast("Assigned"); this.assignTarget = null; await this.loadAll(true); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openComplete(r) { this.completeTarget = r; this.completeNotes = ''; },
    async confirmComplete() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "markBedCleaned", cleaning_id: this.completeTarget.cleaning_id, bed_id: this.completeTarget.bed_id, notes: this.completeNotes }, hms);
        if (r.data && r.data.success) { this.showToast("Bed available"); this.completeTarget = null; await this.loadAll(true); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
